/**
 * Conexión de un proyecto a un sitio existente (SFTP/FTP). El secreto viaja
 * solo hacia el server (nunca vuelve: la API expone hasSecret, no el valor).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

export type SiteProtocol = 'sftp' | 'ftp';
export type SiteAuthMethod = 'password' | 'key';

export interface SiteConnection {
  projectId: number;
  siteUrl: string | null;
  protocol: SiteProtocol;
  host: string;
  port: number | null;
  username: string;
  authMethod: SiteAuthMethod;
  remotePath: string;
  verifiedAt: string | null;
  publishedAt: string | null;
  hasSecret: boolean;
}

export interface SiteConnectionInput {
  siteUrl?: string | null;
  protocol: SiteProtocol;
  host: string;
  port?: number | null;
  username: string;
  authMethod: SiteAuthMethod;
  remotePath: string;
  secret?: string;
}

export type SiteTestStage = 'self_host' | 'host' | 'auth' | 'path' | 'write' | 'ready';

export interface SiteTestResult {
  ok: boolean;
  stage: SiteTestStage;
  detail?: string;
  listing?: Array<{ name: string; type: 'dir' | 'file' }>;
}

export interface EstadoDelSitio {
  connection: SiteConnection | null;
  /** Cuántas páginas tiene el sitio (archivos de página en la carpeta más el sitemap). */
  paginas: number | null;
  /** Lo que Google dice de los sitemaps enviados, o por qué no hay dato. */
  indexadas: { enviadas: number; indexadas: number; sitemaps: number } | null;
  indexadasPorque: 'sin-gsc' | 'sin-sitemap' | 'error' | null;
}

export function useSiteConnection(projectId: number) {
  return useQuery<EstadoDelSitio>({
    queryKey: ['site-connection', projectId],
    queryFn: () => api.get(`/api/projects/${projectId}/site-connection`),
  });
}

export function useSaveSiteConnection(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SiteConnectionInput) =>
      api.put<{ connection: SiteConnection }>(`/api/projects/${projectId}/site-connection`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['site-connection', projectId] });
    },
  });
}

export function useTestSiteConnection(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SiteConnectionInput) =>
      api.post<SiteTestResult>(`/api/projects/${projectId}/site-connection/test`, input),
    onSuccess: result => {
      if (result.ok) void qc.invalidateQueries({ queryKey: ['site-connection', projectId] });
    },
  });
}

/** Lo que una publicación hizo. Es lo que la pantalla muestra después del botón. */
export interface SitePublishResult extends SiteTestResult {
  /** Los archivos que se subieron. */
  aSubir?: string[];
  /** Los que NO se pisaron porque cambiaron en el servidor desde la última importación. */
  cambiadosAfuera?: string[];
  /** Los que no cambiaron desde la última publicación: no se suben ni se tocan. */
  sinCambios?: number;
  respaldoEn?: string;
}

/**
 * Qué pasaría si publicas ahora, sin escribir nada remoto: el mismo
 * `publishSiteFiles` en modo seco. Es lo que la nota previa a publicar lee.
 */
export function usePublishPlan(projectId: number) {
  return useMutation({
    mutationFn: () => api.get<{ plan: SitePublishResult }>(`/api/projects/${projectId}/review`),
  });
}

export function usePublishSite(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<SitePublishResult>(`/api/projects/${projectId}/site-connection/publish`, {}),
    onSuccess: result => {
      if (!result.ok) return;
      void qc.invalidateQueries({ queryKey: ['site-connection', projectId] });
      // Publicar cambia lo que estas pantallas muestran: el análisis de la
      // página, la serie de mediciones (se mide al publicar) y el período.
      void qc.invalidateQueries({ queryKey: ['hallazgos', projectId] });
      void qc.invalidateQueries({ queryKey: ['mantenimiento', projectId] });
      void qc.invalidateQueries({ queryKey: ['periodo', projectId] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export interface SiteImportResult extends SiteTestResult {
  files?: number;
  bytes?: number;
  /** Aviso NO fatal del server: el import trajo algo pero incompleto (se
   *  agotó un tope) o la ruta remota parece la carpeta personal y no el
   *  sitio. Faltaba en este tipo, así que la UI lo descartaba en silencio. */
  warning?: string;
  project?: unknown;
}

export function useImportSite(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<SiteImportResult>(`/api/projects/${projectId}/site-connection/import`, {}),
    onSuccess: result => {
      if (result.ok) {
        void qc.invalidateQueries({ queryKey: ['project'] });
        void qc.invalidateQueries({ queryKey: ['projects'] });
        void qc.invalidateQueries({ queryKey: ['project-versions', projectId] });
        void qc.invalidateQueries({ queryKey: ['site-connection', projectId] });
      }
    },
  });
}
